import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Compass, Search, Loader2, MapPin, Building2, CalendarClock, ExternalLink,
  Sparkles, CheckCircle2, AlertCircle, ListChecks, GraduationCap, Bookmark, BookmarkCheck,
  Trash2, FilePlus2,
} from "lucide-react";

const MATCH_STYLES = {
  stark: { label: "Stark matchning", cls: "bg-green-100 text-green-800 border-green-200" },
  delvis: { label: "Delvis matchning", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  svag: { label: "Svag matchning", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

function fmtDate(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString("sv-SE"); } catch { return null; }
}

export default function Vagledning() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [terms, setTerms] = useState([]);
  const [rationale, setRationale] = useState("");
  const [suggestLoading, setSuggestLoading] = useState(true);
  const [jobs, setJobs] = useState(null);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);

  const [saved, setSaved] = useState([]);
  const savedIds = new Set(saved.map((s) => s.ad_id));

  const [analysis, setAnalysis] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preparingId, setPreparingId] = useState(null);

  useEffect(() => {
    api.post("/vagledning/suggestions")
      .then((r) => {
        const t = r.data.terms || [];
        setTerms(t);
        setRationale(r.data.rationale || "");
        setLocation(r.data.location || "");
        if (t.length) setQ(t[0]);
      })
      .catch(() => {})
      .finally(() => setSuggestLoading(false));
    api.get("/vagledning/saved").then((r) => setSaved(r.data.jobs || [])).catch(() => {});
  }, []);

  const runSearch = async (searchQ) => {
    const query = searchQ !== undefined ? searchQ : q;
    if (!query.trim()) { toast.error("Skriv ett yrke eller sökord"); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/vagledning/jobs", { params: { q: query, location, limit: 20 } });
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const pickTerm = (t) => { setQ(t); runSearch(t); };

  const analyze = async (job) => {
    setAnalyzingId(job.id);
    try {
      const { data } = await api.post(`/vagledning/analyze/${job.id}`);
      setAnalysis(data);
      setDialogOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAnalyzingId(null); }
  };

  const toggleSave = async (job) => {
    if (savedIds.has(job.id)) {
      const prev = saved;
      setSaved((s) => s.filter((x) => x.ad_id !== job.id));
      try { await api.delete(`/vagledning/saved/${job.id}`); }
      catch { setSaved(prev); toast.error("Kunde inte ta bort jobbet"); }
    } else {
      const entry = { ad_id: job.id, title: job.title, employer: job.employer, location: job.location, url: job.url, deadline: job.deadline };
      const prev = saved;
      setSaved((s) => [entry, ...s]);
      try { await api.post("/vagledning/saved", entry); toast.success("Jobb sparat"); }
      catch { setSaved(prev); toast.error("Kunde inte spara jobbet"); }
    }
  };

  const createCvFromJob = async (adId) => {
    setPreparingId(adId);
    try {
      const { data } = await api.get(`/vagledning/prepare-application/${adId}`);
      localStorage.setItem("hamn_prefill_job", JSON.stringify(data));
      navigate("/app/skapa-cv");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPreparingId(null); }
  };

  const ms = analysis && (MATCH_STYLES[analysis.analysis?.match_level] || MATCH_STYLES.delvis);

  return (
    <div>
      <PageHeader
        title="Sök jobb"
        description="Utforska riktiga, aktuella jobbannonser från Platsbanken utifrån din profil. Låt AI:n visa hur väl du matchar, spara jobb du gillar och koppla till riktiga utbildningar – allt grundat i verklig data, aldrig påhittat."
      />

      {/* Sökkort */}
      <Card className="rounded-2xl border-border p-5 md:p-6 mb-6" data-testid="vagledning-search-card">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="vagledning-q-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Yrke eller sökord, t.ex. lagermedarbetare"
              className="pl-9 h-12 rounded-xl"
            />
          </div>
          <div className="relative sm:w-56">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="vagledning-location-input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Ort (valfritt)"
              className="pl-9 h-12 rounded-xl"
            />
          </div>
          <Button data-testid="vagledning-search-btn" onClick={() => runSearch()} disabled={searching} className="h-12 rounded-xl px-6">
            {searching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Sök jobb
          </Button>
        </div>

        {suggestLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-4"><Loader2 className="h-4 w-4 animate-spin" /> Tar fram förslag utifrån din profil…</div>
        ) : terms.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2"><Sparkles className="h-3.5 w-3.5 text-primary" /> Förslag utifrån din profil{rationale ? ` – ${rationale}` : ""}</div>
            <div className="flex flex-wrap gap-2">
              {terms.map((t) => (
                <button
                  key={t}
                  data-testid={`vagledning-term-${t}`}
                  onClick={() => pickTerm(t)}
                  className="rounded-full border border-primary/30 bg-primary/[0.06] px-3 py-1.5 text-sm text-primary font-medium hover:bg-primary/10 transition-colors min-h-[36px]"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Sparade jobb */}
      {saved.length > 0 && (
        <Card className="rounded-2xl border-primary/25 bg-primary/[0.04] p-5 md:p-6 mb-6" data-testid="vagledning-saved-panel">
          <div className="flex items-center gap-2 font-head font-semibold mb-3 text-primary"><BookmarkCheck className="h-5 w-5" /> Sparade jobb ({saved.length})</div>
          <div className="grid gap-3">
            {saved.map((j) => (
              <div key={j.ad_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-white border border-border p-4" data-testid={`vagledning-saved-${j.ad_id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{j.title}</div>
                  <div className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    {j.employer && <span>{j.employer}</span>}
                    {j.location && <span>· {j.location}</span>}
                    {j.deadline && <span>· sök senast {fmtDate(j.deadline)}</span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" className="rounded-full" onClick={() => createCvFromJob(j.ad_id)} disabled={preparingId === j.ad_id} data-testid={`vagledning-saved-createcv-${j.ad_id}`}>
                    {preparingId === j.ad_id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FilePlus2 className="h-4 w-4 mr-1.5" />} Skapa CV
                  </Button>
                  {j.url && <a href={j.url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="rounded-full"><ExternalLink className="h-4 w-4" /></Button></a>}
                  <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={() => toggleSave({ id: j.ad_id })} data-testid={`vagledning-saved-remove-${j.ad_id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Resultat */}
      {jobs === null ? (
        <Card className="rounded-2xl border-dashed border-border p-10 text-center" data-testid="vagledning-empty">
          <Compass className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Klicka på ett förslag ovan eller sök själv för att se aktuella jobb. För varje jobb kan du få en ärlig matchningsanalys, spara det och skapa ett CV direkt.
          </p>
        </Card>
      ) : jobs.length === 0 ? (
        <Card className="rounded-2xl border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">Inga jobb hittades för "{q}"{location ? ` i ${location}` : ""}. Prova ett bredare sökord eller ta bort orten.</p>
        </Card>
      ) : (
        <>
          <div className="text-sm text-muted-foreground mb-3" data-testid="vagledning-results-count">
            {total.toLocaleString("sv-SE")} lediga jobb hittades · visar {jobs.length}
          </div>
          <div className="grid gap-4">
            {jobs.map((job) => (
              <Card key={job.id} className="rounded-2xl border-border p-5" data-testid={`vagledning-job-${job.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-head font-semibold text-base">{job.title}</div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1.5">
                      {job.employer && <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {job.employer}</span>}
                      {job.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {job.location}</span>}
                      {job.deadline && <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Sök senast {fmtDate(job.deadline)}</span>}
                    </div>
                    {job.snippet && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{job.snippet}</p>}
                  </div>
                  <div className="flex flex-row md:flex-col gap-2 shrink-0">
                    <Button
                      data-testid={`vagledning-analyze-${job.id}`}
                      onClick={() => analyze(job)}
                      disabled={analyzingId === job.id}
                      className="rounded-full h-10"
                    >
                      {analyzingId === job.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      Analysera matchning
                    </Button>
                    <Button
                      variant="secondary"
                      data-testid={`vagledning-createcv-${job.id}`}
                      onClick={() => createCvFromJob(job.id)}
                      disabled={preparingId === job.id}
                      className="rounded-full h-10"
                    >
                      {preparingId === job.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FilePlus2 className="h-4 w-4 mr-2" />}
                      Skapa CV utan analys
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className={`rounded-full h-10 flex-1 ${savedIds.has(job.id) ? "text-primary border-primary/40" : ""}`}
                        onClick={() => toggleSave(job)}
                        data-testid={`vagledning-save-${job.id}`}
                      >
                        {savedIds.has(job.id) ? <BookmarkCheck className="h-4 w-4 mr-1.5" /> : <Bookmark className="h-4 w-4 mr-1.5" />}
                        {savedIds.has(job.id) ? "Sparad" : "Spara"}
                      </Button>
                      {job.url && (
                        <a href={job.url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" className="rounded-full h-10" data-testid={`vagledning-open-${job.id}`}>
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Analys-dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vagledning-analysis-dialog">
          {analysis && (
            <>
              <DialogHeader>
                <DialogTitle className="font-head text-xl pr-6">{analysis.ad?.title}</DialogTitle>
                <div className="text-sm text-muted-foreground">{[analysis.ad?.employer, analysis.ad?.location].filter(Boolean).join(" · ")}</div>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                {ms && (
                  <div>
                    <Badge className={`rounded-full border ${ms.cls}`} data-testid="vagledning-match-level">{ms.label}</Badge>
                    <p className="text-sm leading-relaxed mt-3">{analysis.analysis?.match_summary}</p>
                  </div>
                )}
                <Section icon={CheckCircle2} title="Dina styrkor för jobbet" items={analysis.analysis?.strengths} color="text-green-700" />
                <Section icon={AlertCircle} title="Luckor att vara medveten om" items={analysis.analysis?.gaps} color="text-amber-700" />
                <Section icon={ListChecks} title="Konkreta nästa steg" items={analysis.analysis?.action_plan} color="text-primary" ordered />

                {/* Riktiga utbildningar */}
                {(analysis.educations?.length > 0 || analysis.university_search_url) && (
                  <div>
                    <div className="flex items-center gap-2 font-head font-semibold mb-2 text-primary"><GraduationCap className="h-5 w-5" /> Utbildningar som kan hjälpa</div>
                    {analysis.analysis?.education_directions?.length > 0 && (
                      <p className="text-sm text-muted-foreground mb-2">Förslag: {analysis.analysis.education_directions.join(", ")}</p>
                    )}
                    <div className="grid gap-2" data-testid="vagledning-educations">
                      {(analysis.educations || []).map((e, i) => (
                        <a key={i} href={e.url || "#"} target="_blank" rel="noopener noreferrer"
                           className="flex items-start justify-between gap-3 rounded-xl border border-border p-3 hover:border-primary/40 transition-colors">
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{e.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">Yrkeshögskola{e.provider ? ` · ${e.provider}` : ""}{e.extent_text ? ` · ${e.extent_text}` : ""}</div>
                          </div>
                          <ExternalLink className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        </a>
                      ))}
                    </div>
                    {analysis.university_search_url && (
                      <a href={analysis.university_search_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary font-medium mt-2 hover:underline" data-testid="vagledning-university-link">
                        Sök högskole-/universitetsutbildningar på antagning.se <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-3 pt-2 border-t border-border">
                  <Button className="rounded-full" onClick={() => createCvFromJob(analysis.ad.id)} disabled={preparingId === analysis.ad?.id} data-testid="vagledning-dialog-createcv">
                    {preparingId === analysis.ad?.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FilePlus2 className="h-4 w-4 mr-2" />} Skapa CV för detta jobb
                  </Button>
                  {analysis.ad?.url && (
                    <a href={analysis.ad.url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="rounded-full" data-testid="vagledning-dialog-open-ad"><ExternalLink className="h-4 w-4 mr-2" /> Öppna annonsen</Button>
                    </a>
                  )}
                  <Badge variant="secondary" className="rounded-full self-center">Vägledningsunderlag – inte ett facit</Badge>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: Icon, title, items, color, ordered }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className={`flex items-center gap-2 font-head font-semibold mb-2 ${color}`}><Icon className="h-5 w-5" /> {title}</div>
      {ordered ? (
        <ol className="list-decimal ml-5 space-y-1.5 text-sm">{items.map((it, i) => <li key={i}>{it}</li>)}</ol>
      ) : (
        <ul className="space-y-1.5 text-sm">{items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-muted-foreground">•</span> {it}</li>)}</ul>
      )}
    </div>
  );
}
